import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocations, useDeleteLocation } from '@/hooks/useLocations';
import { useClients } from '@/hooks/useClients';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatClientName } from '@jobos/shared';

export default function LocationList() {
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null);

  const { data: clients, isLoading: clientsLoading } = useClients();
  const { data: locations, isLoading: locationsLoading } = useLocations(
    selectedClientId || undefined
  );
  const { mutateAsync: deleteLocation } = useDeleteLocation();

  const handleDelete = async () => {
    if (!deleteLocationId) return;

    try {
      await deleteLocation(deleteLocationId);
      setDeleteLocationId(null);
    } catch (error) {
      console.error('Failed to delete location:', error);
    }
  };

  const isLoading = clientsLoading || locationsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading locations...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Service Addresses</h1>
              <p className="mt-2 text-sm text-gray-700">
                Manage job site service addresses for your clients
              </p>
            </div>
            <Link to="/locations/new">
              <Button>Create New Service Address</Button>
            </Link>
          </div>

          {/* Filter by Client */}
          <div className="mt-6">
            <Select
              value={selectedClientId}
              onValueChange={setSelectedClientId}
            >
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Filter by client (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Clients</SelectItem>
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {formatClientName(client.name, client.firstName, client.lastName)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Service Addresses Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>City</TableHead>
                <TableHead>State</TableHead>
                <TableHead>ZIP</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations && locations.length > 0 ? (
                locations.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell className="font-medium">
                      <Link
                        to={`/clients/${location.clientId}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {/* We need client name - this would require extending the Location type to include client */}
                        View Client
                      </Link>
                    </TableCell>
                    <TableCell>
                      {location.addressLine1}
                      {location.addressLine2 && `, ${location.addressLine2}`}
                    </TableCell>
                    <TableCell>{location.city}</TableCell>
                    <TableCell>{location.state}</TableCell>
                    <TableCell>{location.zip}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/locations/${location.id}`}>
                          <Button variant="outline" size="sm">
                            View
                          </Button>
                        </Link>
                        <Dialog
                          open={deleteLocationId === location.id}
                          onOpenChange={(open: any) =>
                            setDeleteLocationId(open ? location.id : null)
                          }
                        >
                          <DialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              Delete
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Delete Service Address</DialogTitle>
                              <DialogDescription>
                                Are you sure you want to delete this service address at{' '}
                                {location.addressLine1}, {location.city}? This
                                action cannot be undone and will fail if there
                                are jobs associated with this service address.
                              </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                              <Button
                                variant="outline"
                                onClick={() => setDeleteLocationId(null)}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={handleDelete}
                              >
                                Delete
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="text-gray-500">
                      <p className="text-lg font-medium">No service addresses found</p>
                      <p className="text-sm mt-1">
                        {selectedClientId
                          ? 'No service addresses found for the selected client'
                          : 'Get started by creating your first service address'}
                      </p>
                      {!selectedClientId && (
                        <Link to="/locations/new">
                          <Button className="mt-4">Create New Service Address</Button>
                        </Link>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
  );
}
